import { iconWhiteTrash } from "assets/icons";
import { Button } from "components/Button";
import { Card } from "components/Card";
import { CheckBox } from "components/CheckBox";
import { InputFormField } from "components/InputFormField";
import { TextFormField } from "components/TextFormField";
import { useAlert } from "hooks/useAlert";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { useToast } from "hooks/useToast";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Container, FormActions, FormSection, Wrapper } from "./styles";
import { useLocationForm } from "./useLocationForm";

interface LocationFormProps {
  eventId?: string;
  locationId?: string;
}

export const LocationForm: React.FC<LocationFormProps> = ({
  eventId,
  locationId,
}) => {
  const { push } = useHistory();
  const { connectGraphQLApi } = useGraphQLAuth();
  const { setToast } = useToast();
  const { setTextAlertInterface, openAlert, closeAlert, alert } = useAlert();
  const [editingLocation, setEditingLocation] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const onSuccess = () => {
    push(`/events/${eventId}?tab=Locais`);
  };

  const {
    handleInputChange,
    inputsValues,
    touched,
    errors,
    handleSubmit,
    isLoading,
    isEditing,
  } = useLocationForm(onSuccess, editingLocation, eventId);

  const getErrorMessage = (error: any): string | undefined => {
    if (typeof error === "string") return error;
    if (Array.isArray(error)) return error[0];
    return undefined;
  };

  useEffect(() => {
    if (locationId) {
      loadLocationData();
    }
  }, [locationId]);

  useEffect(() => {
    if (alert.typeContinue === "save") {
      executeDeleteLocation();
    }
  }, [alert]);

  const loadLocationData = async () => {
    if (!locationId) return;

    setLoading(true);

    try {
      const query = `
                query BackofficeLocation($locationId: ID!) {
                    backofficeLocation(locationId: $locationId) {
                        locationId
                        eventId
                        name
                        description
                        capacity
                        isActive
                        insertDate
                        updateDate
                    }
                }
            `;

      const response = await connectGraphQLApi({
        query,
        variables: { locationId },
        errorMessage: "Erro ao carregar dados do local",
        useNewApi: true,
      });

      const locationData = response.data.backofficeLocation;
      setEditingLocation(locationData);
    } catch (error) {
      console.error("Error loading location data:", error);
      setToast({
        type: "error",
        message: "Erro ao carregar dados do local.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = () => {
    setTextAlertInterface({
      title: "Excluir Local",
      description:
        "Tem certeza que deseja excluir este local? Esta ação não pode ser desfeita.",
      closeAlertText: "Cancelar",
      continueAlertText: "Excluir",
      closeAlertTypeBtn: "outline",
      continueAlertTypeBtn: "danger",
      positionCenter: true,
      actionNeeded: false,
    });
    openAlert(true, "deleteLocation");
  };

  const executeDeleteLocation = async () => {
    if (!locationId) return;

    try {
      const mutation = `
                mutation BackofficeDeleteLocation($locationId: ID!) {
                    backofficeDeleteLocation(locationId: $locationId)
                }
            `;

      await connectGraphQLApi({
        query: mutation,
        variables: { locationId },
        errorMessage: "Erro ao excluir local",
        useNewApi: true,
      });

      setToast({
        type: "success",
        message: "Local excluído com sucesso!",
        isActive: true,
        timeToRemoveToast: 3000,
      });

      onSuccess();
    } catch (error) {
      setToast({
        type: "error",
        message: "Erro ao excluir local.",
        isActive: true,
        timeToRemoveToast: 5000,
      });
      console.error(error);
    } finally {
      closeAlert();
    }
  };

  if (loading) {
    return (
      <Container>
        <div>Carregando...</div>
      </Container>
    );
  }

  return (
    <Container>
      <form onSubmit={handleSubmit}>
        <Card title="Local" typography="subtitle-medium">
          <FormSection>
            <Wrapper>
              <InputFormField
                label="Nome *"
                name="name"
                value={inputsValues.name}
                onChange={handleInputChange}
                error={touched.name && !!errors.name}
                mensageError={getErrorMessage(errors.name)}
                block
              />

              <TextFormField
                label="Descrição"
                name="description"
                value={inputsValues.description}
                onChange={handleInputChange}
                error={touched.description && !!errors.description}
                mensageError={getErrorMessage(errors.description)}
                block
                rows={3}
              />

              <InputFormField
                label="Capacidade"
                name="capacity"
                type="number"
                value={inputsValues.capacity?.toString() || ""}
                onChange={handleInputChange}
                error={touched.capacity && !!errors.capacity}
                mensageError={getErrorMessage(errors.capacity)}
                block
              />

              <CheckBox
                label="Ativo"
                name="isActive"
                value=""
                checked={inputsValues.isActive}
                onChange={(event) => {
                  const fakeEvent = {
                    target: {
                      name: "isActive",
                      value: event.checked,
                    },
                  };
                  handleInputChange(fakeEvent as any);
                }}
              />
            </Wrapper>
          </FormSection>
        </Card>

        <FormActions>
          {isEditing ? (
            <Button
              label="Excluir Local"
              typeBtn="danger"
              icon={iconWhiteTrash}
              onClick={handleDeleteLocation}
              type="button"
            />
          ) : (
            <div />
          )}
          <div style={{ display: "flex", gap: "1rem" }}>
            <Button
              label="Cancelar"
              typeBtn="outline"
              onClick={() => push(`/events/${eventId}?tab=Locais`)}
              type="button"
            />
            <Button
              label={isEditing ? "Salvar Alterações" : "Criar Local"}
              typeBtn="blue"
              type="submit"
              load={isLoading}
              disabled={isLoading}
            />
          </div>
        </FormActions>
      </form>
    </Container>
  );
};
